/**
 * Catálogo de ubigeo del Perú (departamentos → provincias → distritos).
 *
 * Fuente única para todo lo que necesite elegir un distrito real en vez de que el
 * usuario lo tipee a mano (typos como "Lurin" vs "Lurín" rompen la agrupación).
 * Reutiliza las tablas `geo_ubigeo_*` (mismo origen que el formulario COD).
 *
 * El ubigeo es estático: se carga UNA vez por sesión y queda cacheado a nivel de
 * módulo, así el segundo consumidor no vuelve a pedir las ~1.9k filas.
 */
import { supabase } from '@/lib/supabase';

export interface UbigeoDept { id: string; name: string }
export interface UbigeoProv { id: string; departamento_id: string; name: string }
export interface UbigeoDist { id: string; provincia_id: string; name: string }

export interface UbigeoCatalog {
  depts: UbigeoDept[];
  provs: UbigeoProv[];
  dists: UbigeoDist[];
}

let cache: UbigeoCatalog | null = null;
let inFlight: Promise<UbigeoCatalog> | null = null;

const PAGINA = 1000;

/**
 * Trae todas las filas paginando (los distritos superan el límite de 1000 por request).
 *
 * 🪤 `paginasDeUnaVez` SE PIDEN JUNTAS, no una detrás de otra. El founder (2-oct): «cuando abro el modal de
 *    dirección sale para seleccionar el departamento, la provincia y el distrito, y luego de 3 segundos recién
 *    carga». Los distritos esperaban a que volviera la página 1 para pedir la 2: un viaje de red de más.
 */
async function fetchAllRows<T>(table: string, columns: string, paginasDeUnaVez = 1): Promise<T[]> {
  const pagina = (desde: number) => supabase.from(table).select(columns).order('orden').range(desde, desde + PAGINA - 1);
  const primeras = await Promise.all(Array.from({ length: paginasDeUnaVez }, (_, i) => pagina(i * PAGINA)));
  const out: T[] = [];
  for (const { data, error } of primeras) {
    if (error || !data || data.length === 0) return out;
    out.push(...(data as T[]));
    if (data.length < PAGINA) return out;
  }
  // Más páginas de las pedidas (no pasa con el ubigeo de hoy): el resto, de a una.
  for (let desde = paginasDeUnaVez * PAGINA; ; desde += PAGINA) {
    const { data, error } = await pagina(desde);
    if (error || !data || data.length === 0) break;
    out.push(...(data as T[]));
    if (data.length < PAGINA) break;
  }
  return out;
}

export async function loadUbigeo(): Promise<UbigeoCatalog> {
  if (cache) return cache;
  if (!inFlight) {
    inFlight = (async () => {
      const [depts, provs, dists] = await Promise.all([
        fetchAllRows<UbigeoDept>('geo_ubigeo_departamentos', 'id, name, orden'),
        fetchAllRows<UbigeoProv>('geo_ubigeo_provincias', 'id, departamento_id, name, orden'),
        // ~1.870 distritos: dos páginas, pedidas juntas.
        fetchAllRows<UbigeoDist>('geo_ubigeo_distritos', 'id, provincia_id, name, orden', 2),
      ]);
      cache = { depts, provs, dists };
      return cache;
    })();
  }
  return inFlight;
}

/** Compara ignorando tildes y mayúsculas: "Lurin" === "Lurín". */
export const normalizeUbigeoName = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🚨 EL NOMBRE DE UN LUGAR ES UNA CLAVE, NO UN TEXTO (16-sep-2026)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Un pedido a Los Baños del Inca no ofrecía Shalom. El pedido decía «Cajamarca»; la cobertura de
 * Shalom, «CAJAMARCA» (22 departamentos cargados desde el panel el 10-sep); la fuente oficial,
 * «Cajamarca», «Áncash», «Apurímac». El resolver comparaba texto exacto —`department.eq.…` en
 * PostgREST y siete `===`— y con la mayúscula no coincidía nadie. Cualquier destino fuera de Lima,
 * Cusco y Arequipa tenía a Shalom invisible.
 *
 * 🪤 Y LAS 22 FILAS SE HABÍAN CARGADO PARA ARREGLAR JUSTO ESO. El arreglo no surtió efecto por una
 *    mayúscula, y nadie lo notó: una opción ausente no explica por qué falta.
 *
 * Estas tres funciones son EL criterio, y viven acá —junto al normalizador— para que el resolver,
 * el editor de cobertura y cualquier otro que compare lugares usen el mismo.
 */

/*
 * 🪤 UN LUGAR CON DOS NOMBRES. El catálogo —y la cobertura que se arma con él, como la de Eva— dice «Prov. Const. del
 *    Callao»; una dirección escrita a mano dice «Callao». Es la misma provincia: sin esto, Eva «no llegaba» a ningún
 *    distrito del Callao para esas direcciones (4 en producción, 3-oct). El filtro de agencias ya lo sabía
 *    (`agencyUbigeoFilter`); la clave, que es EL criterio, no.
 */
const OTRO_NOMBRE_DEL_LUGAR: Record<string, string> = {
  'prov. const. del callao': 'callao',
  'provincia constitucional del callao': 'callao',
};

/** Clave comparable de un nombre de lugar. Vacío o `null` → `null` («sin valor»). */
export const claveDeLugar = (s: string | null | undefined): string | null => {
  if (s == null || s.trim() === '') return null;
  const clave = normalizeUbigeoName(s);
  return OTRO_NOMBRE_DEL_LUGAR[clave] ?? clave;
};

/**
 * ¿La fila (cobertura o regla) alcanza este nivel del destino?
 *   · sin valor en la fila → «cualquiera»: alcanza.
 *   · con valor → coincide por CLAVE: «CAJAMARCA», «Cajamarca» y «cajamarca» son el mismo lugar.
 *     Y si el destino no trae ese nivel (`null`), una fila que sí lo exige NO alcanza.
 */
export const mismoLugar = (deLaFila: string | null | undefined, claveDelDestino: string | null): boolean =>
  deLaFila == null || deLaFila.trim() === '' || claveDeLugar(deLaFila) === claveDelDestino;

/**
 * El nombre del CATÁLOGO para lo que se tipeó: «CAJAMARCA» → «Cajamarca». Si no está en el catálogo
 * se devuelve tal cual (recortado): no se inventa un nombre. Para que las tablas de lugares no
 * sigan acumulando tres formas del mismo.
 */
export const nombreDelCatalogo = (tipeado: string, catalogo: ReadonlyArray<{ name: string }>): string => {
  const clave = claveDeLugar(tipeado);
  return catalogo.find((x) => claveDeLugar(x.name) === clave)?.name ?? tipeado.trim();
};

export interface DistrictLocation { province: string; department: string }

/**
 * Provincia y departamento de cada distrito. Se resuelve por distrito (no se asume la
 * ubicación del primero): si alguien elige distritos de provincias distintas, cada zona
 * se guarda con la suya.
 */
export async function resolveDistrictLocations(
  districtNames: string[],
): Promise<Record<string, DistrictLocation>> {
  const { depts, provs, dists } = await loadUbigeo();
  const provById = new Map(provs.map((p) => [p.id, p]));
  const deptById = new Map(depts.map((d) => [d.id, d]));
  const distByName = new Map(dists.map((d) => [normalizeUbigeoName(d.name), d]));

  const out: Record<string, DistrictLocation> = {};
  for (const name of districtNames) {
    const dist = distByName.get(normalizeUbigeoName(name));
    const prov = dist ? provById.get(dist.provincia_id) : undefined;
    const dept = prov ? deptById.get(prov.departamento_id) : undefined;
    out[name] = { province: prov?.name ?? '', department: dept?.name ?? '' };
  }
  return out;
}
